import { useCallback, useEffect, useState } from 'react'
import { DrillValue } from '../components/Drill'
import {
  eraseArchivedAccount,
  listArchives,
  restoreArchivedAccount,
  type RemoteArchive
} from '../lib/adminClient'
import { LoadError } from '../components/LoadError'
import { DetailGrid, ExpandableRow, useOpenRow } from '../components/RowDetail'
import { dateTime } from '../lib/format'
import { useAuth } from '../lib/auth'

// Closed accounts (GET /admin/archives). A person who closes their account, or
// an operator who closes one from Users, archives it: unusable, read by
// nothing, restorable until its erase date, then erased by the server. This
// page is how support acts on a request to connect@plexiidesk.com: restore an
// account, or erase it now. It lists the archive's size, never its contents.

function day(ts: number): string {
  return new Date(ts).toISOString().slice(0, 10)
}

export default function Archives(): JSX.Element {
  const session = useAuth()
  const canWrite = session?.role !== 'support'
  const [archives, setArchives] = useState<RemoteArchive[] | null>(null)
  const [retentionDays, setRetentionDays] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<{ ok: boolean; text: string; details?: string[] } | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const rowOpen = useOpenRow()

  const refresh = useCallback(async () => {
    const res = await listArchives()
    if (res.ok) {
      setArchives(res.data.archives)
      setRetentionDays(res.data.retentionDays)
      setError(null)
    } else {
      setError(res.error)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  async function restore(a: RemoteArchive) {
    if (!confirm(`Restore ${a.email}? The account opens again with its data, on the Free plan. They sign in again on their devices.`)) return
    setBusy(a.id)
    setNotice(null)
    const res = await restoreArchivedAccount(a.id)
    setBusy(null)
    if (!res.ok) {
      setNotice({ ok: false, text: res.error })
      return
    }
    setNotice({
      ok: true,
      text: `${a.email} is restored (${res.data.rows} records). They have been emailed.`,
      details: res.data.notRestored
    })
    await refresh()
  }

  async function erase(a: RemoteArchive) {
    if (!confirm(`ERASE ${a.email} now? Only when they asked for erasure. The archive and its files are deleted for good and cannot be restored.`)) return
    setBusy(a.id)
    setNotice(null)
    const res = await eraseArchivedAccount(a.id)
    setBusy(null)
    if (!res.ok) {
      setNotice({ ok: false, text: res.error })
      return
    }
    setNotice({ ok: true, text: `${a.email} is erased.` })
    await refresh()
  }

  return (
    <div>
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Privacy</div>
        <h1 className="text-2xl font-semibold mt-1">Closed accounts</h1>
        <div className="text-sm text-slate-400 mt-1 max-w-2xl">
          Accounts closed by their owner or here. They cannot be used and nothing reads them.
          {retentionDays !== null ? ` Each is erased ${retentionDays} days after closing.` : ''} Restore one, or erase it
          now, when the person asks (connect@plexiidesk.com).
        </div>
      </div>

      {notice && (
        <div
          role={notice.ok ? 'status' : 'alert'}
          data-testid="archives-notice"
          className={`rounded-xl p-3 mb-4 text-sm border ${notice.ok ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200' : 'bg-red-500/10 border-red-500/30 text-red-200'}`}
        >
          {notice.text}
          {notice.details && notice.details.length > 0 && (
            <ul className="list-disc pl-5 mt-2 space-y-1">
              {notice.details.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {error && <LoadError what="closed accounts" endpoint="GET /admin/archives" error={error} />}

      {archives && archives.length === 0 && (
        <div className="glass-card rounded-xl p-6 text-sm text-slate-400" data-testid="archives-empty">
          No closed accounts.
        </div>
      )}

      {archives && archives.length > 0 && (
        <div className="glass-card rounded-xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="archives-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400">
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Closed</th>
                <th className="px-4 py-3">Erased on</th>
                <th className="px-4 py-3">Closed by</th>
                <th className="px-4 py-3">Size</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {archives.map((a) => (
                <ExpandableRow
                  key={a.id}
                  open={rowOpen.isOpen(a.id)}
                  onToggle={() => rowOpen.toggle(a.id)}
                  colSpan={6}
                  className="border-t border-white/5 hover:bg-white/[0.02]"
                  testId={`archive-row-${a.id}`}
                  detail={() => (
                    <div className="px-4 pb-2">
                      <DetailGrid
                        facts={[
                          ['Email', a.email],
                          ['Account id', <span className="font-mono">{a.accountId}</span>],
                          ['Archive id', <DrillValue to={{ set: 'archives', filters: { id: a.id } }} label={`Archive of ${a.email}`} testId={`archive-record-${a.id}`}><span className="font-mono">{a.id}</span></DrillValue>],
                          ['Closed', dateTime(a.closedAt)],
                          ['Closed by', a.archivedBy === 'self' ? 'The account owner' : a.archivedBy === 'admin' ? 'An operator, from the console' : a.archivedBy],
                          ['Erased on', dateTime(a.eraseAt)],
                          ['Days until erased', Math.max(0, Math.ceil((a.eraseAt - Date.now()) / 86_400_000))],
                          ['Records kept', a.rows.toLocaleString()],
                          ['Files kept', a.files.toLocaleString()]
                        ]}
                      />
                      <div className="text-[11px] text-slate-500 mt-2">Only the size is shown; nothing in the archive is read here.</div>
                    </div>
                  )}
                >
                  <td className="px-4 py-3">{a.email}</td>
                  <td className="px-4 py-3 text-slate-300">{day(a.closedAt)}</td>
                  <td className="px-4 py-3 text-slate-300">{day(a.eraseAt)}</td>
                  <td className="px-4 py-3 text-slate-300">{a.archivedBy === 'self' ? 'themselves' : 'an operator'}</td>
                  <td className="px-4 py-3 text-slate-400">
                    {a.rows.toLocaleString()} records, {a.files.toLocaleString()} files
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {canWrite ? (
                      <>
                        <button
                          className="btn-ghost mr-2"
                          disabled={busy === a.id}
                          onClick={() => void restore(a)}
                          data-testid={`restore-${a.id}`}
                        >
                          Restore
                        </button>
                        <button
                          className="px-3 py-1.5 rounded-md border border-red-500/30 text-xs text-red-300 hover:bg-red-500/10"
                          disabled={busy === a.id}
                          onClick={() => void erase(a)}
                          data-testid={`erase-${a.id}`}
                        >
                          Erase now
                        </button>
                      </>
                    ) : (
                      <span className="text-xs text-slate-500">An admin can restore or erase</span>
                    )}
                  </td>
                </ExpandableRow>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
